import { useViewContext } from "@editor-providers";
import { useIsPdf } from "@library/components/pdf";
import { throttle } from "@library/shared";
import {
  type CSSProperties,
  type MutableRefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTimelineCtx } from "../ctx";
import { useTimelineScrollAnimCtx } from "../ctx/timeline-scroll-anim-ctx";
import { TimelineStyleEnum } from "../types";
import {
  type IPathGenerator,
  makeTimelinePathStyleLine,
  makeTimelinePathStylePath,
} from "../utils";

const DEBUG_CURVE = false;
const DEBUG_CURVE_POINT_CN = "curve-debug-point";

interface IProps {
  itemRef: MutableRefObject<HTMLDivElement | null>;
  itemIdx: number;
}

export interface ITimelineAnimTrackSvgData {
  svgTraceD: MutableRefObject<string>;
  svgPathD: MutableRefObject<string>;
  svgStyle: MutableRefObject<CSSProperties>;
  svgPathStyle: MutableRefObject<CSSProperties>;
  svgTraceStyle: MutableRefObject<CSSProperties>;
}

const INLINE_TO_CSS = {
  height: "height",
  strokeWidth: "stroke-width",
  strokeDashoffset: "stroke-dashoffset",
  strokeDasharray: "stroke-dasharray",
  visibility: "visibility",
  display: "display",
};

const getMappedCssProperty = (
  key: keyof typeof INLINE_TO_CSS,
): string | null => {
  const cssProperty = INLINE_TO_CSS[key];
  if (!cssProperty) return null;
  return cssProperty;
};

type IPathStyleKey = keyof typeof INLINE_TO_CSS;

const setElementStyle = (
  inlineProperty: IPathStyleKey,
  el: HTMLElement | SVGElement,
  ref: MutableRefObject<CSSProperties>,
  value: string,
) => {
  const cssProperty = getMappedCssProperty(inlineProperty);
  if (!cssProperty) return;
  (ref.current[inlineProperty] as unknown) = value;
  el.style.setProperty(cssProperty, value);
};

const removeElementStyle = (
  inlineProperty: IPathStyleKey,
  el: HTMLElement | SVGElement,
  ref: MutableRefObject<CSSProperties>,
) => {
  const cssProperty = getMappedCssProperty(inlineProperty);
  if (!cssProperty) return;
  ref.current[inlineProperty] = undefined;
  el.style.removeProperty(cssProperty);
};

export const useTimelineItemAnim = ({
  itemRef,
  itemIdx,
}: IProps): {
  isItemVisible: boolean;
  trackSvgData: ITimelineAnimTrackSvgData;
} => {
  const svgPathD = useRef("");
  const svgTraceD = useRef("");
  const svgStyle = useRef<CSSProperties>({});
  const svgPathStyle = useRef<CSSProperties>({});
  const svgTraceStyle = useRef<CSSProperties>({});
  const curveDebugPoints = useRef<{ x: number; y: number; fill: string }[]>([]);

  const { view } = useViewContext();
  const isPdf = useIsPdf();

  const {
    isVertical,
    totalAmtOfItems,
    isMobile,
    comboSettings: {
      timelineMarker,
      timelinePosition,
      timelineStyle,
      timelineSetContainer,
      maxWidth,
    },
  } = useTimelineCtx();
  const { canAnimate, scale, addPathUpdateListener, addScrollListener } =
    useTimelineScrollAnimCtx();

  const [isItemVisible, setIsItemVisible] = useState(canAnimate.current);

  const removeCurveDebugPoints = (svgEl: SVGSVGElement) => {
    if (curveDebugPoints.current.length === 0) return;
    const points = svgEl.querySelectorAll(`.${DEBUG_CURVE_POINT_CN}`);
    points.forEach((el) => {
      el.remove();
    });
    curveDebugPoints.current = [];
  };

  useLayoutEffect(() => {
    const itemEl = itemRef.current;
    if (!itemEl || !isVertical) return;

    const svgEl = itemEl.querySelector(".timeline-line-svg") as SVGSVGElement;
    if (!svgEl) {
      console.error("svgEl is undefined! (selector: .timeline-line-svg)");
      return;
    }

    const lineWrapperEl = svgEl.parentElement;
    if (!lineWrapperEl) {
      console.error("lineWrapperEl is undefined!");
      return;
    }

    const traceEl = svgEl.children[0];
    const pathEl = svgEl.children[1];

    if (
      !(pathEl instanceof SVGPathElement) ||
      !(traceEl instanceof SVGPathElement)
    ) {
      console.error("pathEl/traceEl is undefined!)");
      return;
    }

    const markerEl = itemEl.querySelector(".timeline-item-marker");
    if (!markerEl) {
      console.error("markerEl is undefined! (selector: .timeline-item-marker)");
      return;
    }

    const pathWidth = 4;

    // === HELPER FUNCTIONS === \\
    const applyScaleToRect = (rect: DOMRect) => {
      const { x, y } = scale.current;
      rect.x *= x;
      rect.y *= y;
      rect.width *= x;
      rect.height *= y;
    };
    const setSvgStyle = (key: IPathStyleKey, value: string) => {
      setElementStyle(key, svgEl, svgStyle, value);
    };
    const removeSvgStyle = (key: IPathStyleKey) => {
      removeElementStyle(key, svgEl, svgStyle);
    };
    const setPathD = (path: string) => {
      svgPathD.current = path;
      pathEl.setAttribute("d", path);
    };
    const setTraceD = (path: string) => {
      svgTraceD.current = path;
      traceEl.setAttribute("d", path);
    };
    const setPathStyle = (key: IPathStyleKey, value: string) => {
      setElementStyle(key, pathEl, svgPathStyle, value);
    };
    const removePathStyle = (key: IPathStyleKey) => {
      removeElementStyle(key, pathEl, svgPathStyle);
    };
    const setTraceStyle = (key: IPathStyleKey, value: string) => {
      setElementStyle(key, traceEl, svgTraceStyle, value);
    };
    const setPathTraceStyle = (key: IPathStyleKey, value: string) => {
      setPathStyle(key, value);
      setTraceStyle(key, value);
    };
    //
    const cleanUpSvgPath = () => {
      removeSvgStyle("height");
      setTraceD("");
      setPathD("");
      removePathStyle("strokeDashoffset");
      removePathStyle("strokeDasharray");
      removePathStyle("visibility");
      svgTraceD.current = "";
      svgPathD.current = "";
      svgStyle.current = {};
      svgPathStyle.current = {};
      svgTraceStyle.current = {};
    };
    // ======================== \\

    const isLast = itemIdx === totalAmtOfItems - 1;
    const nextItemEl = itemEl.nextElementSibling;

    // stuff like, editorHeader, engagement-bar
    let viewOffetTop = 0;

    const calViewOffsetTop = () => {
      const storyContentEl = document.querySelector("#content-sgKecQsAYKy7MX4");
      if (storyContentEl) {
        const rect = storyContentEl.getBoundingClientRect();
        viewOffetTop = rect.y + window.scrollY;
      }
    };
    calViewOffsetTop();

    let itemRect: DOMRect;
    let pathLength = traceEl.getTotalLength() + 5;

    const generateSvgPath = throttle(() => {
      // when rapdidly changing the dash-offset/array, the path kinda flicers,
      // so I just hide it and then show it on fill
      setPathStyle("visibility", "hidden");

      cleanUpSvgPath();

      // if style is path and item is last, no don't draw anything
      if (!isMobile && timelineStyle === TimelineStyleEnum.Path) {
        if (isLast || !nextItemEl) return;
      }

      itemRect = itemEl.getBoundingClientRect();
      const boxRect = lineWrapperEl.getBoundingClientRect();
      const markerRect = markerEl.getBoundingClientRect();

      applyScaleToRect(itemRect);
      applyScaleToRect(boxRect);
      applyScaleToRect(markerRect);

      // get svgs width
      boxRect.width = svgEl.getBoundingClientRect().width * scale.current.x;

      setSvgStyle("height", `${boxRect.height}px`);

      const drawCurvePoint = (x: number, y: number, fill = "red") => {
        if (!DEBUG_CURVE) return;
        curveDebugPoints.current.push({ x, y, fill });
      };
      removeCurveDebugPoints(svgEl);

      // because of strokeLinecap="round", the actual path is longer by pathWidth / 2
      let pathGenerator: IPathGenerator | null = null;
      if (!isMobile && timelineStyle === TimelineStyleEnum.Path && nextItemEl) {
        pathGenerator = makeTimelinePathStylePath;
      } else {
        pathGenerator = makeTimelinePathStyleLine;
      }

      if (!pathGenerator) {
        return;
      }

      const { path } = pathGenerator({
        applyScaleToRect,
        scale: scale.current,
        pathWidth,
        boxRect,
        markerRect,
        itemRect,
        itemIdx,
        nextItemEl,
        timelineMarker,
        drawCurvePoint,
      });

      // Draw curves's debug points
      if (DEBUG_CURVE) {
        for (const point of curveDebugPoints.current) {
          const newEl = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "circle",
          );
          newEl.setAttribute("r", "4");
          newEl.setAttribute("cx", `${point.x}`);
          newEl.setAttribute("cy", `${point.y}`);
          newEl.setAttribute("fill", point.fill);
          newEl.classList.add(DEBUG_CURVE_POINT_CN);
          svgEl.appendChild(newEl);
          svgEl.getBoundingClientRect();
        }
      }

      setPathTraceStyle("strokeWidth", `${pathWidth}px`);

      setTraceD(path);

      // Important!, we have to trigger layout recalculation before calling getTotlatLength
      // but after setting the trace's d
      traceEl.getBoundingClientRect();
      pathLength = traceEl.getTotalLength() + 5;

      // Set initial visibility based on whether the item is above the fold
      const initiallyVisible = itemRect.top < window.innerHeight;
      setPathStyle(
        "strokeDashoffset",
        canAnimate.current && !initiallyVisible ? pathLength.toString() : "0",
      );
      setPathStyle("strokeDasharray", pathLength.toString());

      setPathD(path);
      applyFillRatio();
    }, 100);

    const applyFillRatio = () => {
      if (!canAnimate.current) {
        setPathStyle("visibility", "visible");
        setIsItemVisible(true);
        return;
      }

      // Check if we're near the end of the page (within 100px of the bottom)
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
      );
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrolledNearBottom =
        documentHeight - (scrollTop + window.innerHeight) < 20;

      // If we're near the bottom, make everything visible
      if (scrolledNearBottom) {
        setPathStyle("visibility", "visible");
        setPathStyle("strokeDashoffset", "0");
        setIsItemVisible(true);
        return;
      }

      // Continue with the regular animation logic for normal scrolling
      const itemRect = itemEl.getBoundingClientRect();
      calViewOffsetTop();

      const itemY = itemRect.y - (window.innerHeight * 0.45 + viewOffetTop);
      let ratio = 1;
      if (itemY <= 0) {
        const maxScrollY = itemRect.height;
        ratio = Math.min(1, Math.max(0, 1 - Math.abs(itemY) / maxScrollY));
      }

      setPathStyle("visibility", "visible");
      setPathStyle("strokeDashoffset", `${pathLength * ratio}`);

      setIsItemVisible(ratio < 1);
    };

    const itemSizeUpdateCleanUp = addPathUpdateListener(
      itemEl,
      generateSvgPath,
    );
    const markerSizeUpdateCleanUp = addPathUpdateListener(
      markerEl,
      generateSvgPath,
    );

    const scrollCelanUp = addScrollListener(() => {
      applyFillRatio();
    });

    // Add resize listener to handle window size changes
    const handleResize = throttle(() => {
      calViewOffsetTop();
      generateSvgPath();
      applyFillRatio();
    }, 200);

    window.addEventListener("resize", handleResize);

    // do the initial generation
    generateSvgPath();

    return () => {
      scrollCelanUp();
      removeCurveDebugPoints(svgEl);
      itemSizeUpdateCleanUp();
      markerSizeUpdateCleanUp();
      window.removeEventListener("resize", handleResize);
      cleanUpSvgPath();
    };
  }, [
    isVertical,
    totalAmtOfItems,
    itemIdx,
    timelineMarker,
    timelineStyle,
    timelinePosition,
    timelineSetContainer,
    maxWidth,
    view,
    isPdf,
    isMobile,
  ]);

  return {
    isItemVisible,
    trackSvgData: {
      svgTraceD,
      svgPathD,
      svgStyle,
      svgPathStyle,
      svgTraceStyle,
    },
  };
};
